import { sessionStore } from "../utils/sessionStore.js";

/**
 * What the agency expects to see after a Stripe page, saved right before leaving:
 * { kind: "plan", plan } | { kind: "seats", quantity } | { kind: "hours", hours, expected }.
 */
const PENDING_KEY = "markezy_stripe_return";

// Back from Stripe the SPA asks `sync/` every 2 s and gives up after 30 s
const STRIPE_SYNC_INTERVAL_MS = 2000;
const STRIPE_SYNC_TIMEOUT_MS = 30000;

export const rememberStripeReturn = (expectation) => sessionStore.set(PENDING_KEY, JSON.stringify(expectation));

export const forgetStripeReturn = () => sessionStore.remove(PENDING_KEY);

export const readStripeReturn = () => {
  try {
    return JSON.parse(sessionStore.get(PENDING_KEY));
  } catch {
    return null;
  }
};

// `?portal=done&flow=…` says which portal page was confirmed; the target comes from the saved expectation.
// `flow=home` (portal home) has no target: one sync.
const FLOW_KINDS = { plan: "plan", seats: "seats" };

export const portalExpectation = (flow, stored) => {
  if (flow === "free") return { kind: "plan", plan: "free" };
  if (flow === "seats_cancel") return { kind: "seats", quantity: 0 };
  return stored?.kind === FLOW_KINDS[flow] ? stored : null;
};

export const checkoutExpectation = (checkout, stored) => {
  if (stored?.kind === "plan" || stored?.kind === "seats") return stored;
  if (checkout.kind === "extra_users") return { kind: "seats", quantity: checkout.extraUsers };
  if (checkout.kind === "plan") return { kind: "plan", plan: checkout.plan };
  return null;
};

export const hoursExpectation = (hours, remainingMinutes) => ({
  kind: "hours",
  hours,
  expected: remainingMinutes + hours * 60,
});

// Balance the saved hours expectation waits for, if it is for this purchase
export const storedHoursTarget = (hours) => {
  const stored = readStripeReturn();
  return stored?.kind === "hours" && stored.hours === hours && Number.isInteger(stored.expected) ? stored.expected : null;
};

// A downgrade or seat reduction shows up as scheduled, not applied
export const isReflected = (expectation, summary) => {
  if (expectation.kind === "plan") {
    if (summary.plan === expectation.plan) return true;
    if (expectation.plan === "free") return summary.cancel_at_period_end;
    return summary.scheduled_plan === expectation.plan;
  }
  const { quantity } = expectation;
  if (quantity === 0) return summary.extra_users === 0 || summary.seats.cancel_at_period_end;
  return summary.extra_users === quantity || summary.scheduled_extra_users === quantity;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs `tick` every STRIPE_SYNC_INTERVAL_MS until `isDone(result)` or the wall-clock deadline, also for a request
 * that hangs. Resolves { status: "done" | "timeout", result }; rejects on the first failed tick; null if cancelled.
 */
export const pollUntil = async ({ tick, isDone, isCancelled = () => false }) => {
  const deadline = Date.now() + STRIPE_SYNC_TIMEOUT_MS;
  let result = null;
  for (;;) {
    const remaining = deadline - Date.now();
    const outcome = await Promise.race([tick().then((value) => ({ value })), wait(remaining).then(() => null)]);
    if (isCancelled()) return null;
    if (!outcome) return { status: "timeout", result };
    result = outcome.value;
    if (isDone(result)) return { status: "done", result };
    if (Date.now() + STRIPE_SYNC_INTERVAL_MS >= deadline) return { status: "timeout", result };
    await wait(STRIPE_SYNC_INTERVAL_MS);
    if (isCancelled()) return null;
  }
};
